import {
  type Translation,
  getISOLocale,
  t,
  tPrice,
} from 'dotcom-core/src/helpers/locale';
import { isNotNullish } from 'dotcom-core/src/helpers/predicate';
import type { ReactElement, ReactNode } from 'react';
import { isValidElement, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useRouteLoaderData } from 'react-router';
import type { BasePrice, BaseSavedPrice, TranslationsQuery } from '~/graphql';

type Callback = () => ReactElement;

function useIntl() {
  const root = useRouteLoaderData('root');
  const ISOLocale = getISOLocale(root.language);

  return {
    tNumber: (value: number, args: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(ISOLocale, args).format(Number(value)),

    tPrice: ({ price }: { price: BasePrice | BaseSavedPrice }) => {
      const translation: Translation = {
        language: ISOLocale,
        country: root.country,
        messages: root.messages,
      };

      return tPrice({ translation, price });
    },

    tDate: (
      value: Date | string | number,
      args?: Intl.DateTimeFormatOptions,
    ) => {
      const dateValue = value instanceof Date ? value : new Date(value);

      if (Number.isNaN(dateValue.getTime())) {
        throw new Error(`Invalid date: ${value}`);
      }

      return new Intl.DateTimeFormat(ISOLocale, args).format(dateValue);
    },

    t: useCallback(
      (
        id: string,
        config?: {
          fallback?: string;
          values?: Record<string, string | number>;
        },
      ): string => {
        const translation: Translation = {
          language: ISOLocale,
          country: root.country,
          messages: root.messages,
        };

        return t(translation, id, config);
      },
      [root.messages, ISOLocale, root.country],
    ),

    tReact: useCallback(
      (
        id: string,
        config?: {
          fallback?: string;
          values?: Record<string, string | Callback | ReactElement>;
        },
      ): ReactNode => {
        let template =
          (root.messages as Record<string, string>)[id] || config?.fallback;

        if (!template) {
          throw new Error(
            `Invalid translation key or fallback provided for id: ${id}.`,
          );
        }

        if (config?.values) {
          template = template.replace(
            /{(\w+)}/g,
            (match: string, key: string) => {
              const value = config?.values?.[key];

              if (isValidElement(value)) {
                return renderToStaticMarkup(value);
              }

              if (value && typeof value === 'function') {
                return renderToStaticMarkup(value());
              }

              if (value && typeof value === 'string') {
                return value;
              }

              return match;
            },
          );
        }

        return <span dangerouslySetInnerHTML={{ __html: template }} />;
      },

      [root.messages],
    ),
  };
}

function flattenMessages(data: TranslationsQuery['translations']):
  | {
      [key: string]: string;
    }
  | undefined {
  const translations = data?.filter(isNotNullish);

  if (translations && translations.length > 0) {
    return Object.fromEntries(
      translations.map(({ key, value }) => [key, value]),
    );
  }
}

export { useIntl, flattenMessages };
